import nonCriticalException from '../../../modules/exceptionLogger';
import {
  ShareChannel,
  shareCurried,
  isAnonymous,
  AuthenticatedShareUser,
} from '../../../modules/share';
import { profileShareParams } from './social/facebook';
import { getSharingLinks } from './social/helpers';
import { ShareProps } from './types';

export type ShareState = {
  share: () => Promise<ShareChannel>;
  isNativeShareAvailable: true;
} | {
  isNativeShareAvailable: false;
};

export async function getNativeShareState({
  target,
  message,
  provider,
  user,
  shorten,
  additionalParams,
  campaign,
  onShareTrigger,
  onShareAbort,
  onShareComplete,
}: ShareProps): Promise<ShareState> {
  let userId: number | 'guest' = 'guest';
  let shareUser: AuthenticatedShareUser | null = null;

  if (!isAnonymous(user)) {
    shareUser = user;
    userId = user.user_id;
  }
  const providerId = provider?.id;

  const getParams = () => {
    const urlParams = [
      { key: 'utm_medium', value: 'simple_share_button' },
      { key: 'utm_campaign', value: `${campaign || 'sharing_referral'}_${userId}` },
    ];

    if (providerId) urlParams.push({ key: 'utm_term', value: `provider_${providerId}` });

    return [...urlParams, ...additionalParams || message?.urlParams || []];
  };

  const createShareLinks = async () => getSharingLinks(
    target,
    providerId,
    shorten,
    [{
      id: 'simple_share_button',
      parameters: getParams(),
    }],
  );

  const urls = await createShareLinks();
  if (!urls?.simple_share_button) {
    return {
      isNativeShareAvailable: false,
    };
  }

  let share: () => Promise<ShareChannel>;
  const params = profileShareParams(
    provider,
    urls.simple_share_button,
    shareUser,
    message,
  );

  try {
    const shareCb = await shareCurried(
      params,
      (targetPlatform?: ShareChannel) => {
        // Share was successful
        if (onShareComplete) onShareComplete(targetPlatform);
      },
      err => {
        // Share failed / cancelled
        if (onShareAbort) onShareAbort(err);
      },
    );
    share = () => {
      if (onShareTrigger) onShareTrigger();
      return shareCb();
    };
  } catch (err) {
    if (!(err instanceof Error) || !err.message.includes('No native share')) {
      nonCriticalException(err);
    }
    return {
      isNativeShareAvailable: false,
    };
  }
  return {
    share,
    isNativeShareAvailable: true,
  };
}
